import { Alert, Button, Card, Col, Empty, Form, Input, InputNumber, Modal, notification, Popconfirm, Radio, Row, Space, Spin, Tabs, Tag, Tooltip } from "antd";
import {
    ArrowDownOutlined, ArrowLeftOutlined, ArrowUpOutlined, DeleteOutlined, FileTextOutlined,
    PlusOutlined, SwapOutlined, UnorderedListOutlined
} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/es_ES";
import { AntdDatePicker, InternalPageLayout } from "components";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
    correctLetterCounts, countAttempts, defaultGradingScale, DraftQuestion, emptyQuestion, getQuizWithKey,
    GradeRange, MIN_OPTIONS, nextQuizDate, OPTION_LETTERS, QUIZ_WEEKDAY, QuizStatus, saveQuiz, shuffleAnswers,
    statusLabels, toDraftQuestions, validateQuestions
} from "./quizModel";
import { parseQuizText, ParsedQuiz } from "./quizParser";
import useQuizAccess from "./useQuizAccess";

const { TextArea } = Input;
const MANUAL_TAB = "manual";
const IMPORT_TAB = "import";

interface QuestionCardProps {
    question: DraftQuestion;
    index: number;
    total: number;
    onChange: (question: DraftQuestion) => void;
    onRemove: () => void;
    onMove: (direction: -1 | 1) => void;
}

const QuestionCard = ({ question, index, total, onChange, onRemove, onMove }: QuestionCardProps) => {
    const setOption = (i: number, text: string) => onChange({ ...question, options: question.options.map((o, oi) => (oi === i ? text : o)) });
    const removeOption = (i: number) => {
        const correctIndex = question.correctIndex === i ? -1 : question.correctIndex > i ? question.correctIndex - 1 : question.correctIndex;
        onChange({ ...question, options: question.options.filter((_, oi) => oi !== i), correctIndex });
    };

    return (
        <Card
            size="small"
            style={{ marginBottom: 12, borderColor: question.correctIndex < 0 ? "#FFD591" : undefined }}
            title={<span>{`Pregunta ${index + 1}`}</span>}
            extra={
                <Space size={4}>
                    <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onMove(-1)} />
                    <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={index === total - 1} onClick={() => onMove(1)} />
                    <Popconfirm title="¿Eliminar pregunta?" okText="Eliminar" cancelText="Cancelar" onConfirm={onRemove}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            }
        >
            <TextArea
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="Texto de la pregunta"
                value={question.text}
                onChange={(e) => onChange({ ...question, text: e.target.value })}
                style={{ marginBottom: 10 }}
            />
            {question.options.map((option, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <Tooltip title="Respuesta correcta">
                        <Radio checked={question.correctIndex === i} onChange={() => onChange({ ...question, correctIndex: i })} />
                    </Tooltip>
                    <span style={{ fontWeight: 600, width: 16 }}>{OPTION_LETTERS[i]}</span>
                    <Input
                        value={option}
                        placeholder={`Opción ${OPTION_LETTERS[i]}`}
                        onChange={(e) => setOption(i, e.target.value)}
                        style={{ borderColor: question.correctIndex === i ? "#52C41A" : undefined }}
                    />
                    <Button
                        size="small"
                        type="text"
                        icon={<DeleteOutlined />}
                        disabled={question.options.length <= MIN_OPTIONS}
                        onClick={() => removeOption(i)}
                    />
                </div>
            ))}
            {question.options.length < OPTION_LETTERS.length && (
                <Button size="small" type="link" icon={<PlusOutlined />} onClick={() => onChange({ ...question, options: [...question.options, ""] })}>
                    Agregar opción
                </Button>
            )}
        </Card>
    );
};

const QuizEditorForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [questions, setQuestions] = useState<DraftQuestion[]>([]);
    const [gradingScale, setGradingScale] = useState<GradeRange[]>([]);
    const [status, setStatus] = useState<QuizStatus>("draft");
    const [attempts, setAttempts] = useState(0);
    const [isLoading, setIsLoading] = useState(Boolean(id));
    const [isSaving, setIsSaving] = useState(false);
    const [tab, setTab] = useState(id ? MANUAL_TAB : IMPORT_TAB);
    const [importText, setImportText] = useState("");
    const [parsed, setParsed] = useState<ParsedQuiz | null>(null);
    const quizDate: Dayjs | undefined = Form.useWatch("quizDate", form);

    useEffect(() => {
        if (!id) {
            form.setFieldsValue({ quizDate: nextQuizDate() });
            return;
        }
        Promise.all([getQuizWithKey(id), countAttempts(id).catch(() => 0)])
            .then(([result, count]) => {
                if (!result) {
                    notification.error({ message: "Cuestionario no encontrado" });
                    navigate("/quiz/manage");
                    return;
                }
                const { quiz, key } = result;
                form.setFieldsValue({
                    title: quiz.title,
                    description: quiz.description,
                    level: quiz.level,
                    estimatedMinutes: quiz.estimatedMinutes,
                    quizDate: quiz.quizDate ? dayjs(quiz.quizDate) : nextQuizDate()
                });
                setQuestions(toDraftQuestions(quiz, key));
                setGradingScale(quiz.gradingScale);
                setStatus(quiz.status);
                setAttempts(count);
            })
            .catch((error) => {
                console.error(error);
                notification.error({ message: "Error", description: "No se pudo cargar el cuestionario" });
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const letterCounts = useMemo(() => correctLetterCounts(questions), [questions]);

    const updateQuestion = (index: number, question: DraftQuestion) => setQuestions((prev) => prev.map((q, i) => (i === index ? question : q)));
    const moveQuestion = (index: number, direction: -1 | 1) => setQuestions((prev) => {
        const next = [...prev];
        [next[index], next[index + direction]] = [next[index + direction], next[index]];
        return next;
    });

    const analyze = () => setParsed(parseQuizText(importText));

    const applyParsed = () => {
        if (!parsed) return;
        const apply = () => {
            const current = form.getFieldsValue();
            form.setFieldsValue({
                title: parsed.title || current.title,
                description: parsed.description || current.description,
                level: parsed.level || current.level,
                estimatedMinutes: parsed.estimatedMinutes ?? current.estimatedMinutes
            });
            setQuestions(parsed.questions);
            setGradingScale(parsed.gradingScale);
            setTab(MANUAL_TAB);
            notification.success({ message: `${parsed.questions.length} preguntas cargadas`, description: "Revísalas antes de guardar." });
        };
        if (questions.length) {
            Modal.confirm({
                title: "Reemplazar preguntas",
                content: `Se reemplazarán las ${questions.length} preguntas actuales por las ${parsed.questions.length} del texto.`,
                okText: "Reemplazar",
                cancelText: "Cancelar",
                onOk: apply
            });
        } else {
            apply();
        }
    };

    const save = async (nextStatus: QuizStatus) => {
        let meta;
        try {
            meta = await form.validateFields();
        } catch {
            return;
        }
        const errors = validateQuestions(questions);
        if (gradingScale.some((g) => !g.label.trim())) errors.push("Todas las calificaciones de la escala necesitan nombre.");
        if (errors.length) {
            notification.warning({
                message: "Revisa el cuestionario",
                description: <ul style={{ paddingLeft: 18, margin: 0 }}>{errors.slice(0, 6).map((e) => <li key={e}>{e}</li>)}{errors.length > 6 && <li>{`y ${errors.length - 6} más…`}</li>}</ul>
            });
            return;
        }
        setIsSaving(true);
        try {
            await saveQuiz(id ?? null, meta, questions, gradingScale, nextStatus);
            notification.success({ message: "Cuestionario guardado" });
            navigate("/quiz/manage");
        } catch (error) {
            console.error(error);
            notification.error({ message: "Error", description: "No se pudo guardar el cuestionario" });
            setIsSaving(false);
        }
    };

    const setGrade = (index: number, patch: Partial<GradeRange>) => setGradingScale((prev) => prev.map((g, i) => (i === index ? { ...g, ...patch } : g)));

    const manualTab = (
        <>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: "#5E5058" }}>Respuestas correctas por letra:</span>
                {letterCounts.map(({ letter, count }) => (
                    <Tag key={letter} color={count ? "magenta" : "default"}>{`${letter}: ${count}`}</Tag>
                ))}
                <div style={{ flex: 1 }} />
                <Popconfirm
                    title="Se cambiará el orden de las opciones para repartir la respuesta correcta entre todas las letras."
                    okText="Revolver"
                    cancelText="Cancelar"
                    disabled={!questions.length}
                    onConfirm={() => setQuestions(shuffleAnswers(questions))}
                >
                    <Button icon={<SwapOutlined />} disabled={!questions.length}>Revolver respuestas</Button>
                </Popconfirm>
                <Button icon={<PlusOutlined />} onClick={() => setQuestions((prev) => [...prev, emptyQuestion()])}>Agregar pregunta</Button>
            </div>
            {questions.length ? questions.map((q, i) => (
                <QuestionCard
                    key={q.id}
                    question={q}
                    index={i}
                    total={questions.length}
                    onChange={(next) => updateQuestion(i, next)}
                    onRemove={() => setQuestions((prev) => prev.filter((_, qi) => qi !== i))}
                    onMove={(direction) => moveQuestion(i, direction)}
                />
            )) : <Empty description="Sin preguntas. Agrega una o impórtalas desde texto." />}
        </>
    );

    const importTab = (
        <>
            <p style={{ color: "#5E5058", fontSize: 13, marginTop: 0 }}>
                Pega el cuestionario tal como llegó por WhatsApp: encabezado, preguntas numeradas (<code>1.</code>) con opciones
                (<code>A)</code>), la tabla de calificación (<code>18–20 correctas Excelente</code>) y la clave
                (<code>Respuestas correctas</code> seguido de <code>1 B</code>, <code>2 C</code>…). También puedes marcar la correcta con ✅.
            </p>
            <TextArea rows={14} value={importText} onChange={(e) => setImportText(e.target.value)} placeholder="Pega aquí el texto…" />
            <Space style={{ marginTop: 12 }}>
                <Button icon={<FileTextOutlined />} onClick={analyze} disabled={!importText.trim()}>Analizar texto</Button>
                {parsed && parsed.questions.length > 0 && <Button type="primary" onClick={applyParsed}>Usar estas preguntas</Button>}
            </Space>
            {parsed && (
                <div style={{ marginTop: 16 }}>
                    <Alert
                        type={parsed.questions.length ? (parsed.warnings.length ? "warning" : "success") : "error"}
                        showIcon
                        message={parsed.questions.length
                            ? `${parsed.questions.length} preguntas · clave para ${parsed.questions.filter((q) => q.correctIndex >= 0).length} · ${parsed.gradingFromText ? "escala del texto" : "escala por defecto"}`
                            : "No se detectaron preguntas"}
                        description={parsed.warnings.length ? <ul style={{ paddingLeft: 18, margin: 0 }}>{parsed.warnings.map((w) => <li key={w}>{w}</li>)}</ul> : undefined}
                    />
                    {parsed.title && <p style={{ marginTop: 12, marginBottom: 0 }}><strong>Título:</strong> {parsed.title}</p>}
                    {parsed.questions.slice(0, 3).map((q, i) => (
                        <div key={q.id} style={{ marginTop: 8, fontSize: 13 }}>
                            <strong>{`${i + 1}. ${q.text}`}</strong>
                            {q.options.map((o, oi) => (
                                <div key={oi} style={{ color: oi === q.correctIndex ? "#389E0D" : "#5E5058", fontWeight: oi === q.correctIndex ? 600 : 400 }}>
                                    {`${OPTION_LETTERS[oi]}) ${o}`}
                                </div>
                            ))}
                        </div>
                    ))}
                    {parsed.questions.length > 3 && <p style={{ color: "#8C8C8C", fontSize: 12 }}>{`… y ${parsed.questions.length - 3} más`}</p>}
                </div>
            )}
        </>
    );

    return (
        <InternalPageLayout title={id ? "Editar cuestionario" : "Nuevo cuestionario"}>
            <Spin spinning={isLoading}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/quiz/manage")}>Volver</Button>
                    {id && <Tag color={statusLabels[status].color}>{statusLabels[status].label}</Tag>}
                </div>
                {attempts > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={`Este cuestionario ya tiene ${attempts} intento(s).`}
                        description="Las respuestas se guardan por letra: cambiar opciones, la clave o revolver las respuestas altera la lectura de los resultados existentes."
                    />
                )}

                <Card title="Datos generales" size="small" style={{ marginBottom: 16 }}>
                    <Form form={form} layout="vertical">
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item name="title" label="Título" rules={[{ required: true, whitespace: true, message: "Escribe un título" }]}>
                                    <Input placeholder="Quiz de Cultura y Excelencia" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item
                                    name="quizDate"
                                    label="Fecha del quiz"
                                    rules={[{ required: true, message: "Selecciona la fecha" }]}
                                    extra={quizDate && quizDate.day() !== QUIZ_WEEKDAY ? "La fecha no cae en martes." : undefined}
                                >
                                    <AntdDatePicker locale={locale} format="dddd DD/MM/YYYY" style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Item name="estimatedMinutes" label="Minutos">
                                    <InputNumber min={1} max={240} style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Item name="level" label="Nivel">
                                    <Input placeholder="Intermedio" />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item name="description" label="Descripción" style={{ marginBottom: 0 }}>
                                    <TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                <Card title={`Preguntas (${questions.length})`} size="small" style={{ marginBottom: 16 }}>
                    <Tabs activeKey={tab} onChange={setTab}>
                        <Tabs.TabPane key={MANUAL_TAB} tab={<span><UnorderedListOutlined /> Captura manual</span>}>{manualTab}</Tabs.TabPane>
                        <Tabs.TabPane key={IMPORT_TAB} tab={<span><FileTextOutlined /> Importar desde texto</span>}>{importTab}</Tabs.TabPane>
                    </Tabs>
                </Card>

                <Card
                    title="Escala de calificación"
                    size="small"
                    style={{ marginBottom: 16 }}
                    extra={<Button size="small" type="link" onClick={() => setGradingScale(defaultGradingScale(questions.length))} disabled={!questions.length}>Recalcular (90/80/70%)</Button>}
                >
                    {gradingScale.map((g, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                            <InputNumber min={0} value={g.min} onChange={(v) => setGrade(i, { min: Number(v ?? 0) })} style={{ width: 80 }} />
                            <span>a</span>
                            <InputNumber min={0} value={g.max} onChange={(v) => setGrade(i, { max: Number(v ?? 0) })} style={{ width: 80 }} />
                            <span>correctas</span>
                            <Input value={g.label} onChange={(e) => setGrade(i, { label: e.target.value })} style={{ width: 240 }} placeholder="Calificación" />
                            <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => setGradingScale((prev) => prev.filter((_, gi) => gi !== i))} />
                        </div>
                    ))}
                    <Button size="small" icon={<PlusOutlined />} onClick={() => setGradingScale((prev) => [...prev, { min: 0, max: 0, label: "" }])}>
                        Agregar rango
                    </Button>
                </Card>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button onClick={() => navigate("/quiz/manage")}>Cancelar</Button>
                    <Button loading={isSaving} onClick={() => save(id ? status : "draft")}>
                        {id ? "Guardar cambios" : "Guardar borrador"}
                    </Button>
                    {status !== "active" && (
                        <Button type="primary" loading={isSaving} onClick={() => save("active")}>Guardar y activar</Button>
                    )}
                </div>
            </Spin>
        </InternalPageLayout>
    );
};
// Sin perfil Admin el editor no se abre ni por URL directa.
const QuizEditor = () => {
    const { canManage } = useQuizAccess();
    return canManage ? <QuizEditorForm /> : <Navigate replace to="/quiz/manage" />;
};
export default QuizEditor;
